import { Colors } from "@/enums/Colors";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { DocumentCenterFileSmall } from "@/models/DocumentCenterFileSmall";
import DocumentCenterFileService from "@/services/DocumentCenterFileService";
import { Add, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, List, ListItem, TextField, Typography, styled } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonButton from "../utils/CommonButton";
import CommonFormControl from "../utils/CommonFormControl";
import LazoModal from "../utils/LazoModal";
import { FolderNotch } from "../utils/icons/FolderNotch";

interface FileBrowserRecursiveListProps {
  options: DocumentCenterFileSmall[];
  onItemClick: (pos: string, externalId: string, refresh?: boolean) => void;
  pos: string;
  collapsed: boolean;
  selectedFolder: DocumentCenterFileSmall | null;
  tenantExternalId?: string;
  lazoUserExternalId?: string;
}

interface RecursiveListItemProps {
  item: DocumentCenterFileSmall;
  index: number;
  pos: string;
  onItemClick: (pos: string, externalId: string, refresh?: boolean) => void;
  selectedFolder: DocumentCenterFileSmall | null;
  tenantExternalId?: string;
  lazoUserExternalId?: string;
}

interface AddFolderOptionProps {
  parentId: string;
  tenantExternalId?: string;
  lazoUserExternalId?: string;
  onItemClick: () => void;
}

function RecursiveListItem({
  item,
  index,
  pos,
  onItemClick,
  selectedFolder,
  tenantExternalId,
  lazoUserExternalId,
}: RecursiveListItemProps) {
  const [innerCollapsed, setInnerCollapsed] = useState<boolean>(false);

  const currentPos = useMemo(() => {
    return pos ? `${pos},${index}` : index.toString();
  }, [pos, index]);

  const paddingLeft = useMemo(() => {
    return pos ? `${pos.split(",").length * 4 + 2}px` : "2px";
  }, [pos]);

  const isSelectedFolder = useMemo(() => {
    return selectedFolder?.externalId === item.externalId;
  }, [selectedFolder?.externalId, item.externalId]);

  const handleItemClick = useCallback(() => {
    setInnerCollapsed((prev) => !prev);
    onItemClick(currentPos, item.externalId);
  }, [currentPos, item.externalId, onItemClick]);

  const handleAddFolderClick = useCallback(() => {
    // This clears the useState hooks preventing an error of differing hook numbers when refreshing folders
    setInnerCollapsed(false);
    onItemClick(currentPos, item.externalId, true);
  }, [currentPos, item.externalId, onItemClick]);

  const hasChildren = Boolean(item.children && item.children.length > 0);

  return (
    <React.Fragment key={item.externalId}>
      <StyledListItem
        sx={{
          paddingLeft,
          ...(isSelectedFolder && {
            backgroundColor: Colors.BUTTON_SECONDARY_PURPLE,
          }),
        }}
      >
        <NameContainer onClick={handleItemClick}>
          <FolderNotch />
          <Typography>{item.name}</Typography>
          {innerCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
        </NameContainer>
        <AddFolderOption
          parentId={item.externalId}
          tenantExternalId={tenantExternalId}
          lazoUserExternalId={lazoUserExternalId}
          onItemClick={handleAddFolderClick}
        />
      </StyledListItem>
      {hasChildren && (
        <FileBrowserRecursiveList
          collapsed={innerCollapsed}
          pos={currentPos}
          options={item.children!}
          onItemClick={onItemClick}
          selectedFolder={selectedFolder}
          tenantExternalId={tenantExternalId}
          lazoUserExternalId={lazoUserExternalId}
        />
      )}
    </React.Fragment>
  );
}

function FileBrowserRecursiveList(props: FileBrowserRecursiveListProps) {
  const { options, collapsed, pos, onItemClick, selectedFolder, tenantExternalId, lazoUserExternalId } = props;

  const listKey = useMemo(() => pos || "initial", [pos]);

  if (!collapsed) {
    return null;
  }

  return (
    <List key={listKey}>
      {options?.map((item, index) => (
        <RecursiveListItem
          key={item.externalId}
          item={item}
          index={index}
          pos={pos}
          onItemClick={onItemClick}
          selectedFolder={selectedFolder}
          tenantExternalId={tenantExternalId}
          lazoUserExternalId={lazoUserExternalId}
        />
      ))}
    </List>
  );
}

function AddFolderOption({ parentId, tenantExternalId, lazoUserExternalId, onItemClick }: AddFolderOptionProps) {
  const { t } = useTranslation("documentCenter");
  const [nameModal, setNameModal] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>("");

  const handleClose = useCallback(() => {
    setNameModal(false);
    setNewName("");
  }, []);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) {
      return;
    }

    try {
      const response = await DocumentCenterFileService.createFolder(
        newName,
        parentId,
        tenantExternalId,
        undefined,
        undefined,
        lazoUserExternalId
      );
      response.onSuccess(() => {
        onItemClick();
        setNameModal(false);
        setNewName("");
      });
      response.onError((message) => {
        NotificationService.error(t("errors.creatingFolder") + message);
      });
    } catch (error) {
      NotificationService.error(t("errors.creatingFolder") + String(error));
    }
  }, [newName, parentId, tenantExternalId, lazoUserExternalId, onItemClick, t]);

  return (
    <>
      <AddFolderButton onClick={() => setNameModal(true)}>
        <Add />
      </AddFolderButton>
      {nameModal && (
        <LazoModal
          open={nameModal}
          title={t("modals.createFolder.title")}
          handleClose={handleClose}
          buttons={[
            <CommonButton key="cancel" purpleBorderVariant sx={{ borderRadius: "0.5rem" }} onClick={handleClose}>
              {t("common:cancel")}
            </CommonButton>,
            <CommonButton key="create" sx={{ borderRadius: "0.5rem" }} onClick={handleCreate} disabled={!newName.trim()}>
              {t("common:create")}
            </CommonButton>,
          ]}
        >
          <CommonFormControl>
            <TextField
              placeholder={t("modals.createFolder.placeholder")}
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && newName.trim()) {
                  handleCreate();
                }
              }}
            />
          </CommonFormControl>
        </LazoModal>
      )}
    </>
  );
}

const StyledListItem = styled(ListItem)(() => ({
  justifyContent: "space-between",
  cursor: "default",
}));

const NameContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flex: 1,
  gap: "0.25rem",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.8,
  },
}));

const AddFolderButton = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  borderRadius: "100%",
  cursor: "pointer",
  padding: "4px",
  "&:hover": {
    backgroundColor: Colors.BUTTON_HOVER_PURPLE,
    color: Colors.WHITE,
    transition: "all 0.1s ease",
  },
}));

// Memoize the main component for performance
const MemoizedFileBrowserRecursiveList = React.memo(FileBrowserRecursiveList);

export default MemoizedFileBrowserRecursiveList;
